/**
 * Timezone utilities — pure, environment-agnostic, DST-correct.
 *
 * The repo already reads wall-clock parts in an IANA zone via `Intl`
 * (`loyalty/streak.ts`, `promotions/resolver.ts`). Appointment booking needs
 * the INVERSE too — turning a tenant-local `YYYY-MM-DD` + `HH:MM` into an epoch
 * instant — so slot start times are stored/compared as `Timestamp`-friendly
 * millis regardless of where the Cloud Function or the browser is running.
 *
 * All functions are pure (same inputs → same output). They rely only on `Intl`
 * (Node ships full ICU; every target browser has it) and `Date.UTC`, never on
 * the ambient process timezone.
 */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const HHMM_RE = /^\d{2}:\d{2}$/;

/**
 * Offset (in ms) of `timeZone` at the given instant, such that
 * `wallClockAsUtcMs === instantMs + offset`. Positive east of UTC.
 */
function zoneOffsetMs(instantMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(instantMs));
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return asUtc - instantMs;
}

/**
 * Convert a wall-clock time in `timeZone` to an epoch-ms instant (DST-correct).
 *
 * Algorithm: treat the wall clock as if it were UTC (the "guess"), measure the
 * zone's offset AT that guess, subtract it, then refine once (handles the DST
 * transition edge where the offset at the guess differs from the offset at the
 * real instant). For fixed-offset zones like `Asia/Kuala_Lumpur` (+8, no DST)
 * the first pass is already exact.
 */
export function zonedTimeToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  const off1 = zoneOffsetMs(guess, timeZone);
  let instant = guess - off1;
  const off2 = zoneOffsetMs(instant, timeZone);
  if (off2 !== off1) instant = guess - off2;
  return instant;
}

/** Parse a strict `YYYY-MM-DD` string into numeric parts. Throws on malformed input. */
export function parseDateISO(dateISO: string): { year: number; month: number; day: number } {
  if (!ISO_DATE_RE.test(dateISO)) {
    throw new RangeError(`Invalid dateISO (expected YYYY-MM-DD): ${dateISO}`);
  }
  const [year, month, day] = dateISO.split('-').map(Number) as [number, number, number];
  return { year, month, day };
}

/** `YYYY-MM-DD` + `HH:MM` (both tenant-local, in `timeZone`) → epoch ms. */
export function zonedDateTimeToUtcMs(dateISO: string, hhmm: string, timeZone: string): number {
  if (!HHMM_RE.test(hhmm)) {
    throw new RangeError(`Invalid time (expected HH:MM): ${hhmm}`);
  }
  const { year, month, day } = parseDateISO(dateISO);
  const [hour, minute] = hhmm.split(':').map(Number) as [number, number];
  return zonedTimeToUtcMs(year, month, day, hour, minute, timeZone);
}

/** The calendar date (`YYYY-MM-DD`) at `instantMs`, evaluated in `timeZone`. */
export function zonedDateISO(instantMs: number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(instantMs));
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Add `days` to a `YYYY-MM-DD` string using calendar arithmetic (the instant
 * is irrelevant — `Date.UTC` is used only as a stable day counter, so DST is a
 * non-issue). Negative `days` subtracts.
 */
export function addDaysISO(dateISO: string, days: number): string {
  const { year, month, day } = parseDateISO(dateISO);
  const ms = Date.UTC(year, month - 1, day) + days * 86_400_000;
  const dt = new Date(ms);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
